import {Component, forwardRef, OnInit, OnChanges, SimpleChanges, Input} from '@angular/core';
import { NG_VALUE_ACCESSOR, Validators, FormGroup, FormControl, FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {compareMatch,  SecondaryIndustrySelectionValue, GetIndustryGroupsByDivisionCodeFn, IndustryGroup} from '@portal-workspace/grow-shared-library';
 
import {UntilDestroy} from '@ngneat/until-destroy';
import {formControlErrorKeys, formControlErrorMessage, setupUntilDestroy, PortalHotToastService} from '@portal-workspace/grow-ui-library';
import {Subscription, of} from 'rxjs';
import {distinctUntilChanged, tap, catchError} from 'rxjs/operators';
import {AbstractControlValueAccessor} from '../abstract-control-value-accessor';
import {MARK, Mark} from '@portal-workspace/grow-ui-library/mark';
import { MatOptionModule } from '@angular/material/core';

import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';


@UntilDestroy()
@Component({
    selector: 'secondary-industry-selection',
    templateUrl: './secondary-industry-selection.component.html',
    styleUrls: ['./secondary-industry-selection.component.scss'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SecondaryIndustrySelectionComponent), multi: true },
        { provide: MARK, useExisting: forwardRef(() => SecondaryIndustrySelectionComponent) },
    ],
    standalone: true,
    imports: [FormsModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, MatOptionModule]
})
export class SecondaryIndustrySelectionComponent extends AbstractControlValueAccessor<SecondaryIndustrySelectionValue> implements OnInit, OnChanges, Mark {

  formGroup: FormGroup<{
    selection: FormControl<SecondaryIndustrySelectionValue>;
  }>;
  formControl: FormControl<SecondaryIndustrySelectionValue>;

  subscription?: Subscription;
  formControlSubscription: Subscription;
  errorKeys = formControlErrorKeys;
  errorMessage = formControlErrorMessage;

  secondaryIndustries: IndustryGroup[] = [];
  @Input({ required: true }) divisionCode: string | null = null;
  @Input({ required: true }) getIndustryGroupsByDivisionCodeFn: GetIndustryGroupsByDivisionCodeFn | null = null;
  compareWithFn: (option: SecondaryIndustrySelectionValue, selected: SecondaryIndustrySelectionValue) => boolean = (option, selected) => {
    return (!!option && !!selected && option.code === selected.code);
  };


  constructor(private formBuilder: FormBuilder,
              private toastService: PortalHotToastService) {
    super();
    this.formControl = formBuilder.control(null, [Validators.required]);
    this.formGroup = formBuilder.group({
      selection: this.formControl,
    });
    this.formControlSubscription = this.formControl.valueChanges.pipe(
      distinctUntilChanged(compareMatch),
      tap(r => {
        this.propagateChange(r);
      })
    ).subscribe();
    console.log("divisionCode", this.divisionCode);
  }

  ngOnInit(): void {
    setupUntilDestroy(this);
    console.log("divisionCode on init", this.divisionCode);
    // Load initial data if divisionCode is already set
    if (this.divisionCode && this.getIndustryGroupsByDivisionCodeFn) {
      this.loadSecondaryIndustries(this.divisionCode);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Detect when divisionCode input changes
    if (changes['divisionCode']) {
      const newDivisionCode = changes['divisionCode'].currentValue;
      const previousDivisionCode = changes['divisionCode'].previousValue;
      
      console.log("divisionCode changed from", previousDivisionCode, "to", newDivisionCode);
      
      if (newDivisionCode && newDivisionCode !== previousDivisionCode) {
        // Clear current selection when division changes
        this.formControl.setValue(null);
        this.secondaryIndustries = [];
        
        // Load new secondary industries
        if (this.getIndustryGroupsByDivisionCodeFn) {
          this.loadSecondaryIndustries(newDivisionCode);
        }
      } else if (!newDivisionCode) {
        // Clear data when divisionCode is cleared
        this.formControl.setValue(null);
        this.secondaryIndustries = [];
      }
    }
  }

  private loadSecondaryIndustries(divisionCode: string): void {
    if (this.getIndustryGroupsByDivisionCodeFn) {
      this.getIndustryGroupsByDivisionCodeFn(divisionCode).pipe(
        this.toastService.spinnerObservable(),
        tap((industryGroups: IndustryGroup[]) => {
          this.secondaryIndustries = industryGroups;
          console.log('Secondary industries loaded for division', industryGroups);
        }),
        catchError(error => {
          console.error('Error loading secondary industries:', error);
          this.toastService.error('Failed to load industry sectors', 'Error');
          this.secondaryIndustries = [];
          return of([]);
        })
      ).subscribe();
    }
  }

  doWriteValue(v: SecondaryIndustrySelectionValue | null): void | SecondaryIndustrySelectionValue {
    if (v) {
      const o = this.secondaryIndustries.find(opt => opt.code === v.code);
      if (o) {
        this.formControl.setValue(o);
        return o;
      }else {
        this.formControl.setValue(v);
        return v;
      }
    } 
    return undefined;
  }
  mark() {
    this.formGroup.markAllAsTouched();
  }
}
